import { DEFAULT_SCENE_OPTION } from "../stores/sceneDefaults";
import { usePhysicsStore } from "../stores/physicsStore";
import type {
  SceneCollisionMode,
  SceneCone,
  SceneIntegrator,
  SceneJacobian,
  SceneOption,
  SceneSolver,
  Vector3,
} from "../types/scene";
import { logger } from "./logger";

const SUPPORTED_INTEGRATORS = new Set<SceneIntegrator>([
  "Euler",
  "RK4",
  "implicit",
  "implicitfast",
]);
const SUPPORTED_SOLVERS = new Set<SceneSolver>(["Newton", "CG", "PGS"]);
const SUPPORTED_COLLISION_MODES = new Set<SceneCollisionMode>(["dynamic", "all", "predefined"]);
const SUPPORTED_CONES = new Set<SceneCone>(["pyramidal", "elliptic"]);
const SUPPORTED_JACOBIANS = new Set<SceneJacobian>(["auto", "dense", "sparse"]);

const normalizeIntegrator = (value: unknown): SceneIntegrator => {
  const raw = String(value || "").trim();
  const lower = raw.toLowerCase();
  if (lower === "euler") return "Euler";
  if (lower === "rk4") return "RK4";
  if (SUPPORTED_INTEGRATORS.has(raw as SceneIntegrator)) return raw as SceneIntegrator;
  return DEFAULT_SCENE_OPTION.integrator;
};

const parseVector3 = (value: unknown, fallback: Vector3): Vector3 => {
  const parts = Array.isArray(value)
    ? value.map(Number)
    : String(value || "")
        .trim()
        .split(/\s+/)
        .map(Number);
  if (parts.length < 3 || parts.slice(0, 3).some((part) => !Number.isFinite(part))) {
    return [...fallback] as Vector3;
  }
  return [parts[0], parts[1], parts[2]] as Vector3;
};

const parseNumericList = (value: unknown): number[] => {
  const parts = Array.isArray(value)
    ? value.map(Number)
    : String(value || "")
        .trim()
        .split(/\s+/)
        .map(Number);
  return parts.filter((part) => Number.isFinite(part));
};

const parseVector2 = (value: unknown, fallback: [number, number]): [number, number] => {
  const parts = parseNumericList(value);
  if (parts.length < 2) return [...fallback] as [number, number];
  return [parts[0], parts[1]] as [number, number];
};

const parseVector5 = (
  value: unknown,
  fallback: [number, number, number, number, number]
): [number, number, number, number, number] => {
  const parts = parseNumericList(value);
  if (parts.length < 3) return [...fallback] as [number, number, number, number, number];
  return [
    parts[0],
    parts[1],
    parts[2],
    Number.isFinite(parts[3]) ? parts[3] : fallback[3],
    Number.isFinite(parts[4]) ? parts[4] : fallback[4],
  ] as [number, number, number, number, number];
};

const normalizeSolver = (value: unknown): SceneSolver => {
  const raw = String(value || "").trim();
  const normalized = raw ? raw[0].toUpperCase() + raw.slice(1).toLowerCase() : "";
  if (SUPPORTED_SOLVERS.has(normalized as SceneSolver)) return normalized as SceneSolver;
  return DEFAULT_SCENE_OPTION.solver;
};

const normalizeCone = (value: unknown): SceneCone => {
  const raw = String(value || "")
    .trim()
    .toLowerCase();
  if (SUPPORTED_CONES.has(raw as SceneCone)) return raw as SceneCone;
  return DEFAULT_SCENE_OPTION.cone;
};

const normalizeCollisionMode = (value: unknown): SceneCollisionMode => {
  const raw = String(value || "")
    .trim()
    .toLowerCase();
  if (SUPPORTED_COLLISION_MODES.has(raw as SceneCollisionMode)) return raw as SceneCollisionMode;
  return DEFAULT_SCENE_OPTION.collision;
};

const normalizeJacobian = (value: unknown): SceneJacobian => {
  const raw = String(value || "")
    .trim()
    .toLowerCase();
  if (SUPPORTED_JACOBIANS.has(raw as SceneJacobian)) return raw as SceneJacobian;
  return DEFAULT_SCENE_OPTION.jacobian;
};

const positiveNumber = (value: unknown, fallback: number): number => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const nonNegativeNumber = (value: unknown, fallback: number): number => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
};

const positiveInteger = (value: unknown, fallback: number): number => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : fallback;
};

const nonNegativeInteger = (value: unknown, fallback: number): number => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : fallback;
};

export function normalizeSceneOption(input?: Partial<SceneOption> | null): SceneOption {
  const source = input || {};
  const rawFlags = source.flags;
  const normalizedFlags: Record<string, string> = {};
  if (rawFlags && typeof rawFlags === "object") {
    Object.entries(rawFlags as Record<string, unknown>).forEach(([key, value]) => {
      if (!key) return;
      if (value === undefined || value === null) return;
      const next = String(value).trim();
      if (!next) return;
      normalizedFlags[key] = next;
    });
  }
  return {
    timestep: positiveNumber(source.timestep, DEFAULT_SCENE_OPTION.timestep),
    gravity: parseVector3(source.gravity, DEFAULT_SCENE_OPTION.gravity),
    wind: parseVector3(source.wind, DEFAULT_SCENE_OPTION.wind),
    integrator: normalizeIntegrator(source.integrator),
    iterations: positiveInteger(source.iterations, DEFAULT_SCENE_OPTION.iterations),
    tolerance: positiveNumber(source.tolerance, DEFAULT_SCENE_OPTION.tolerance),
    solver: normalizeSolver(source.solver),
    collision: normalizeCollisionMode(source.collision),
    cone: normalizeCone(source.cone),
    jacobian: normalizeJacobian(source.jacobian),
    impratio: positiveNumber(source.impratio, DEFAULT_SCENE_OPTION.impratio),
    noslip_iterations: nonNegativeInteger(
      source.noslip_iterations,
      DEFAULT_SCENE_OPTION.noslip_iterations
    ),
    noslip_tolerance: positiveNumber(
      source.noslip_tolerance,
      DEFAULT_SCENE_OPTION.noslip_tolerance
    ),
    mpr_iterations: nonNegativeInteger(source.mpr_iterations, DEFAULT_SCENE_OPTION.mpr_iterations),
    mpr_tolerance: positiveNumber(source.mpr_tolerance, DEFAULT_SCENE_OPTION.mpr_tolerance),
    density: nonNegativeNumber(source.density, DEFAULT_SCENE_OPTION.density),
    viscosity: nonNegativeNumber(source.viscosity, DEFAULT_SCENE_OPTION.viscosity),
    o_margin: nonNegativeNumber(source.o_margin, DEFAULT_SCENE_OPTION.o_margin),
    o_solref: parseVector2(source.o_solref, DEFAULT_SCENE_OPTION.o_solref),
    o_solimp: parseVector5(source.o_solimp, DEFAULT_SCENE_OPTION.o_solimp),
    flags: normalizedFlags,
  };
}

export function parseMjcfOption(xmlDoc: Document): SceneOption {
  const optionElem = xmlDoc.querySelector("mujoco > option, option");
  if (!optionElem) return normalizeSceneOption();
  const flagElem =
    Array.from(optionElem.children).find((child) => child.tagName.toLowerCase() === "flag") || null;
  const flagAttrs: Record<string, string> = {};
  if (flagElem) {
    Array.from(flagElem.attributes).forEach((attr) => {
      const key = String(attr.name || "").trim();
      const value = String(attr.value || "").trim();
      if (!key || !value) return;
      flagAttrs[key] = value;
    });
  }
  return normalizeSceneOption({
    timestep: optionElem.getAttribute("timestep") ?? undefined,
    gravity: optionElem.getAttribute("gravity") ?? undefined,
    wind: optionElem.getAttribute("wind") ?? undefined,
    integrator: optionElem.getAttribute("integrator") ?? undefined,
    iterations: optionElem.getAttribute("iterations") ?? undefined,
    tolerance: optionElem.getAttribute("tolerance") ?? undefined,
    solver: optionElem.getAttribute("solver") ?? undefined,
    cone: optionElem.getAttribute("cone") ?? undefined,
    jacobian: optionElem.getAttribute("jacobian") ?? undefined,
    impratio: optionElem.getAttribute("impratio") ?? undefined,
    noslip_iterations: optionElem.getAttribute("noslip_iterations") ?? undefined,
    noslip_tolerance: optionElem.getAttribute("noslip_tolerance") ?? undefined,
    mpr_iterations:
      optionElem.getAttribute("ccd_iterations") ??
      optionElem.getAttribute("mpr_iterations") ??
      undefined,
    mpr_tolerance:
      optionElem.getAttribute("ccd_tolerance") ??
      optionElem.getAttribute("mpr_tolerance") ??
      undefined,
    density: optionElem.getAttribute("density") ?? undefined,
    viscosity: optionElem.getAttribute("viscosity") ?? undefined,
    o_margin: optionElem.getAttribute("o_margin") ?? undefined,
    o_solref: optionElem.getAttribute("o_solref") ?? undefined,
    o_solimp: optionElem.getAttribute("o_solimp") ?? undefined,
    flags: flagAttrs,
  } as Partial<SceneOption>);
}

export function extractMjcfOption(xmlText: string): SceneOption | null {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, "application/xml");
    if (xmlDoc.querySelector("parsererror")) return null;
    return parseMjcfOption(xmlDoc);
  } catch (error) {
    logger.warn("[mjcfOptions] Failed to extract MJCF option", error);
    return null;
  }
}

export function normalizeMjcfOptionAttributesForMujoco3(xmlText: string): string {
  return String(xmlText || "").replace(
    /<\s*option\b([^>]*?)(\/?)>/gi,
    (_full, attrs, selfClose) => {
      let nextAttrs = String(attrs || "");
      nextAttrs = nextAttrs.replace(/\s+collision\s*=\s*"[^"]*"/gi, "");
      nextAttrs = nextAttrs.replace(/\s+mpr_iterations\s*=\s*"([^"]*)"/gi, (_match, value) =>
        /\sccd_iterations\s*=/i.test(nextAttrs) ? "" : ` ccd_iterations="${value}"`
      );
      nextAttrs = nextAttrs.replace(/\s+mpr_tolerance\s*=\s*"([^"]*)"/gi, (_match, value) =>
        /\sccd_tolerance\s*=/i.test(nextAttrs) ? "" : ` ccd_tolerance="${value}"`
      );
      return `<option${nextAttrs}${selfClose || ""}>`;
    }
  );
}

export function extractMjcfTimestep(xmlText: string): number | null {
  const match = xmlText.match(/<\s*option\b[^>]*\btimestep\s*=\s*"([^"]+)"/i);
  const value = match?.[1] != null ? Number(match[1]) : NaN;
  return Number.isFinite(value) && value > 0 ? value : null;
}

export async function applyPhysicsTimestepFromXmlFile(
  xmlFile: File,
  sourceLabel = "MJCF"
): Promise<number | null> {
  try {
    const text = await xmlFile.text();
    const option = extractMjcfOption(text);
    const timestep = option?.timestep ?? extractMjcfTimestep(text);
    if (timestep == null) return null;
    const actions = usePhysicsStore.getState().actions;
    actions.setTimestep(timestep);
    if (option) {
      actions.setSolverIterations(option.iterations);
      actions.setSolverTolerance(option.tolerance);
    }
    logger.info(`[physics] Using ${sourceLabel} timestep: ${timestep}`);
    return timestep;
  } catch (error) {
    logger.warn(`[physics] Failed to read ${sourceLabel} timestep`, error);
    return null;
  }
}
